import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useEffect, useState, type JSX } from 'react';

import { SolicitationAttachmentsField } from '@/presentation/pages/developments/solicitations/solicitation-attachments-field.js';

interface ResolveSolicitationDialogLabels {
    markResolved: string;
    resolutionNotes: string;
    cancel: string;
    resolve: string;
    attachments: string;
    attachmentsHint: string;
    addFiles: string;
    invalidFileFormat: (files: string) => string;
    fileTooLarge: (files: string) => string;
    tooManyFiles: string;
}

export interface ResolveSolicitationDialogProps {
    open: boolean;
    resolving: boolean;
    uploading: boolean;
    onCancel: () => void;
    onResolve: (notes: string, files: File[]) => Promise<boolean>;
    labels: ResolveSolicitationDialogLabels;
}

export function ResolveSolicitationDialog({ open, resolving, uploading, onCancel, onResolve, labels }: ResolveSolicitationDialogProps): JSX.Element {
    const [notes, setNotes] = useState('');
    const [files, setFiles] = useState<File[]>([]);

    useEffect(() => {
        if (open) {
            setNotes('');
            setFiles([]);
        }
    }, [open]);

    const busy = resolving || uploading;

    const handleResolve = async () => {
        try {
            await onResolve(notes, files);
        } catch (error) {
            console.error(error);
        }
    };

    const handleClose = () => {
        if (!busy) {
            onCancel();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} disableEscapeKeyDown={busy} maxWidth="xs" fullWidth data-testid="resolve-solicitation-dialog">
            <DialogTitle>{labels.markResolved}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField label={labels.resolutionNotes} value={notes} onChange={(e) => setNotes(e.target.value)} size="small" multiline rows={2} fullWidth />
                    <SolicitationAttachmentsField files={files} onFilesChange={setFiles} disabled={busy} labels={labels} />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} disabled={busy} data-testid="resolve-solicitation-cancel">
                    {labels.cancel}
                </Button>
                <Button onClick={handleResolve} variant="contained" sx={{ color: 'white' }} disabled={busy} data-testid="resolve-solicitation-submit">
                    {labels.resolve}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
